import { errorMessages } from '~/constants/errorMessages.ts';
import * as Yup from 'yup';
import { regularExpressions } from '~/constants/regularExpressions';

export const stepSchema = (
  isExists: boolean,
  step: number,
  isSoleTrader: boolean,
  isCompleted: boolean,
  isCRSIFopEnabled: boolean,
  isAdditionalInformationAboutTaxResidencyEnabled: boolean,
  isOwnershipStructureTreeEnabled: boolean
) =>
  Yup.object().shape({
    identificationNumber: Yup.string()
      .nullable()
      .required(errorMessages.FIELD_REQUIRED)
      .min(8, errorMessages.INVALID_IDENTIFICATION_NUMBER)
      .max(10, errorMessages.INVALID_IDENTIFICATION_NUMBER)
      .test('is-numeric', errorMessages.MUST_BE_NUMBER, function (value) {
        if (value && value.trim() !== '') {
          if (!regularExpressions.IDENTIFICATION_NUMBER.test(value)) {
            return this.createError({
              path: 'identificationNumber',
              message: errorMessages.INVALID_IDENTIFICATION_NUMBER,
            });
          }
        }
        return true;
      }),
    applicationNumber: Yup.string()
      .nullable()
      .when([], {
        is: () => isExists && step,
        then: (s) =>
          s
            .required(errorMessages.APPLICATION_NUMBER_REQUIRED)
            .length(36, errorMessages.INVALID_APPLICATION_NUMBER)
            .test('valid-appId', function (value) {
              if (!regularExpressions.APPLICATION_ID.test(value)) {
                return this.createError({
                  path: 'applicationNumber',
                  message: errorMessages.INVALID_APPLICATION_NUMBER,
                });
              }
              return true;
            }),
        otherwise: (s) => s.notRequired(),
      }),
    isUSTaxResident: Yup.boolean()
      .nullable()
      .when([], {
        is: () => !isExists && step && !isCompleted,
        then: (s) => s.required(errorMessages.YES_NO_REQUIRED),
        otherwise: (s) => s.notRequired(),
      }),
    isForeignFinancialUnderFATCA: Yup.boolean()
      .nullable()
      .when([], {
        is: () => !isExists && step && !isCompleted,
        then: (s) => s.required(errorMessages.YES_NO_REQUIRED),
        otherwise: (s) => s.notRequired(),
      }),
    isOtherTaxResident: Yup.boolean()
      .nullable()
      .when([], {
        is: () =>
          isSoleTrader
            ? !isExists && step && !isCompleted && !isCRSIFopEnabled
            : !isAdditionalInformationAboutTaxResidencyEnabled
              ? !isExists && step && !isCompleted
              : false,
        then: (s) => s.required(errorMessages.YES_NO_REQUIRED),
        otherwise: (s) => s.notRequired(),
      }),
    proxyAccountManagers: Yup.boolean()
      .nullable()
      .when([], {
        is: () =>
          !isSoleTrader
            ? !isExists && step && !isCompleted && !isOwnershipStructureTreeEnabled
            : !isExists && step && !isCompleted,
        then: (s) => s.required(errorMessages.YES_NO_REQUIRED),
        otherwise: (s) => s.notRequired(),
      }),
    hasForeignShare: Yup.boolean()
      .nullable()
      .when([], {
        is: () => !isExists && step && !isSoleTrader && !isCompleted,
        then: (s) => s.required(errorMessages.YES_NO_REQUIRED),
        otherwise: (s) => s.notRequired(),
      }),
    tariffPackage: Yup.object()
      .shape({
        value: Yup.string().nullable().required(errorMessages.OPTION_REQUIRED),
        label: Yup.string().required(errorMessages.OPTION_REQUIRED),
      })
      .nullable()
      .when([], {
        is: () => !isExists && step,
        then: (s) => s.required(errorMessages.OPTION_REQUIRED),
        otherwise: (s) => s.notRequired(),
      }),
    region: Yup.object()
      .shape({
        value: Yup.string().nullable().required(errorMessages.OPTION_REQUIRED),
        label: Yup.string().required(errorMessages.OPTION_REQUIRED),
      })
      .nullable()
      .when([], {
        is: () => !isExists && step,
        then: (s) => s.required(errorMessages.OPTION_REQUIRED),
        otherwise: (s) => s.notRequired(),
      }),
    tobo: Yup.object()
      .shape({
        value: Yup.string().nullable().required(errorMessages.OPTION_REQUIRED),
        label: Yup.string().required(errorMessages.OPTION_REQUIRED),
      })
      .nullable()
      .when([], {
        is: () => !isExists && step,
        then: (s) => s.required(errorMessages.OPTION_REQUIRED),
        otherwise: (s) => s.notRequired(),
      }),
    currency: Yup.array()
      .of(
        Yup.object()
          .shape({
            value: Yup.string().nullable().required(errorMessages.OPTION_REQUIRED),
            label: Yup.string().required(errorMessages.OPTION_REQUIRED),
          })
          .nullable()
          .test('empty-currency', function (value) {
            if (!step && isExists) return true;
            if ((!value?.label || !value?.value) && value !== null) {
              return this.createError({
                path: this.path,
                message: errorMessages.OPTION_REQUIRED,
              });
            }
            return true;
          })
      )
      .when([], {
        is: () => !isExists && step,
        then: (s) => s.required(errorMessages.OPTION_REQUIRED),
        otherwise: (s) => s.notRequired(),
      }),
    contragentType: Yup.object()
      .shape({
        value: Yup.string().nullable(),
        label: Yup.string().nullable(),
      })
      .nullable()
      .notRequired(),
    verification: Yup.number().notRequired(),
    contragentId: Yup.number().nullable(),
  });

export const stepSchemaReIdentification = (
  isExists: boolean,
  step: number,
  isSoleTrader: boolean,
  isCompleted: boolean,
  isCRSIFopEnabled: boolean,
  isAdditionalInformationAboutTaxResidencyEnabled: boolean,
  isOwnershipStructureTreeEnabled: boolean
) =>
  Yup.object().shape({
    identificationNumber: Yup.string()
      .nullable()
      .required(errorMessages.FIELD_REQUIRED)
      .min(8, errorMessages.INVALID_IDENTIFICATION_NUMBER)
      .max(10, errorMessages.INVALID_IDENTIFICATION_NUMBER)
      .test('is-numeric', errorMessages.MUST_BE_NUMBER, function (value) {
        if (value && value.trim() !== '') {
          if (!regularExpressions.IDENTIFICATION_NUMBER.test(value)) {
            return this.createError({
              path: 'identificationNumber',
              message: errorMessages.INVALID_IDENTIFICATION_NUMBER,
            });
          }
        }
        return true;
      }),
    applicationNumber: Yup.string()
      .nullable()
      .when([], {
        is: () => isExists && step,
        then: (s) =>
          s
            .required(errorMessages.APPLICATION_NUMBER_REQUIRED)
            .length(36, errorMessages.INVALID_APPLICATION_NUMBER)
            .test('valid-appId', function (value) {
              if (!regularExpressions.APPLICATION_ID.test(value)) {
                return this.createError({
                  path: 'applicationNumber',
                  message: errorMessages.INVALID_APPLICATION_NUMBER,
                });
              }
              return true;
            }),
        otherwise: (s) => s.notRequired(),
      }),
    isUSTaxResident: Yup.boolean()
      .nullable()
      .when([], {
        is: () => !isExists && step && !isCompleted,
        then: (s) => s.required(errorMessages.YES_NO_REQUIRED),
        otherwise: (s) => s.notRequired(),
      }),
    isForeignFinancialUnderFATCA: Yup.boolean()
      .nullable()
      .when([], {
        is: () => !isExists && step && !isCompleted,
        then: (s) => s.required(errorMessages.YES_NO_REQUIRED),
        otherwise: (s) => s.notRequired(),
      }),
    isOtherTaxResident: Yup.boolean()
      .nullable()
      .when([], {
        is: () =>
          isSoleTrader
            ? !isExists && step && !isCompleted && !isCRSIFopEnabled
            : !isAdditionalInformationAboutTaxResidencyEnabled
              ? !isExists && step && !isCompleted
              : false,
        then: (s) => s.required(errorMessages.YES_NO_REQUIRED),
        otherwise: (s) => s.notRequired(),
      }),
    proxyAccountManagers: Yup.boolean()
      .nullable()
      .when([], {
        is: () => !isExists && step && !isCompleted && !isOwnershipStructureTreeEnabled,
        then: (s) => s.required(errorMessages.YES_NO_REQUIRED),
        otherwise: (s) => s.notRequired(),
      }),
    hasForeignShare: Yup.boolean()
      .nullable()
      .when([], {
        is: () => !isExists && step && !isSoleTrader && !isCompleted,
        then: (s) => s.required(errorMessages.YES_NO_REQUIRED),
        otherwise: (s) => s.notRequired(),
      }),
    contragentType: Yup.object()
      .shape({
        value: Yup.string().nullable(),
        label: Yup.string().nullable(),
      })
      .nullable()
      .notRequired(),
    verification: Yup.number().notRequired(),
    contragentId: Yup.number().nullable(),
  });
